/**
 * DOOH adapter — per-type import adapter for 'dooh' media type.
 *
 * Unlike every other adapter, `requiredColumns`/`optionalColumns` are
 * validated as a subset of `DOOH_NOSNIKI_HEADERS` (the DOOH template's own
 * header list), not `NOSNIKI_HEADERS` — see xlsx-import-template spec's
 * "Per-type required-column source of truth is the adapter registry"
 * requirement, dooh carve-out.
 *
 * Required columns: exactly COMMON_REQUIRED_COLUMNS — the same common set
 * every other adapter requires, with no dooh-specific additions (spec:
 * "DOOH adapter validation and parsing").
 *
 * validate() additionally enforces:
 * - Numer dostawcy and both coordinate columns are present, citing the
 *   Polish header as the error field. Format/Miejscowość/Adres/Dostawca are
 *   NOT re-checked here even though they're also required columns —
 *   validateRow() (validate-row.ts) already enforces those unconditionally
 *   for every adapter's row on every real call path (both the worker's
 *   import job, which short-circuits before reaching adapter.validate() if
 *   validateRow() already failed, and the API's dry-run collectRowErrors(),
 *   which concatenates validateRow() + adapter.validate()). Re-checking
 *   them here would duplicate the same error for the user.
 * - spot duration / loop size / screens count, when present, are positive
 *   integers
 *
 * parseRow() populates the digital and screen fields from the mapped row on
 * top of buildBaseRow's null defaults. `Outdoor` parses via the shared
 * parseBool helper; an unparseable value yields null without an error.
 */
import type { MediaUnitImportAdapter, ValidationError } from './types.js';
import type { MappedRow } from '../validate-row.js';
import {
  buildBaseRow,
  parseBool,
  parseDecimal,
  parseInteger,
  trim,
  validatePositiveInteger,
  validateRequiredField,
  COMMON_REQUIRED_COLUMNS,
} from './parse-row.helper.js';

const REQUIRED_COLUMNS = COMMON_REQUIRED_COLUMNS;

const OPTIONAL_COLUMNS = [
  'Numer Grupy RW',
  'Typ nośnika',
  'Województwo',
  'Aglomeracja',
  'Opis lokalizacji',
  'Liczba ekranów w lokalizacji',
  'Wielkość ekranu [cale]',
  'Outdoor',
  '[Digital] Czas jednej reklamy [s]',
  '[Digital] Ile reklam w pętli',
  '[Digital] Godziny pracy ekranu',
  '[Digital] Rozdzielczość ekranu',
  '[Digital] Dzienna liczba emisji',
  '[Digital] Liczba emisji total',
  '[Digital] Cena 1 spota',
  'Czynsz',
  'Koszt ekspozycji',
  'Cena katalogowa',
  'Cena po rabacie',
  'RW na start',
  'Koszty dodatkowe',
  'Produkcja',
  'Uwagi',
  'Tag 1',
  'Tag 2',
  'Tag 3',
  'Tag 4',
  'Tag 5',
] as const;

/**
 * [dbColumn, Polish header label] pairs for the common required columns
 * NOT already enforced by validateRow() (city/address/name ARE already
 * enforced there for every adapter — see the validate() doc comment above).
 */
const COMMON_REQUIRED_FIELD_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['supplier_number', 'Numer dostawcy'],
  ['latitude', 'Współrzędne - szerokość'],
  ['longitude', 'Współrzędne - długość'],
];

export const doohAdapter: MediaUnitImportAdapter = {
  mediaType: 'dooh',
  requiredColumns: REQUIRED_COLUMNS,
  optionalColumns: OPTIONAL_COLUMNS,

  validate(row: MappedRow): ValidationError[] {
    const errors: ValidationError[] = [];
    for (const [dbColumn, polishLabel] of COMMON_REQUIRED_FIELD_LABELS) {
      validateRequiredField(errors, row[dbColumn], dbColumn, polishLabel);
    }
    validatePositiveInteger(
      errors,
      row['digital_spot_duration_seconds'],
      'digital_spot_duration_seconds',
      '[Digital] Czas jednej reklamy [s]',
    );
    validatePositiveInteger(
      errors,
      row['digital_loop_size'],
      'digital_loop_size',
      '[Digital] Ile reklam w pętli',
    );
    validatePositiveInteger(
      errors,
      row['screens_count'],
      'screens_count',
      'Liczba ekranów w lokalizacji',
    );
    return errors;
  },

  parseRow(row: MappedRow) {
    return {
      ...buildBaseRow(row, 'dooh'),
      digitalSpotDurationSeconds: parseInteger(row['digital_spot_duration_seconds']),
      digitalLoopSize: parseInteger(row['digital_loop_size']),
      digitalScreenResolution: trim(row['digital_screen_resolution']),
      digitalScreenHours: trim(row['digital_screen_hours']),
      digitalDailyEmissions: parseInteger(row['digital_daily_emissions']),
      digitalTotalCampaignEmissions: parseInteger(row['digital_total_campaign_emissions']),
      digitalSpotPrice: parseDecimal(row['digital_spot_price']),
      screensCount: parseInteger(row['screens_count']),
      isOutdoor: parseBool(row['is_outdoor']),
      digitalScreenSizeInches: parseDecimal(row['digital_screen_size_inches']),
    };
  },
};
